import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthManagerService } from '../auth-manager.service';
import { ProductService } from '../product.service';
import { DiwaliCartLine, DiwaliCartService } from './diwali-cart.service';

@Component({
  selector: 'app-diwali-voucher',
  templateUrl: './diwali-voucher.component.html',
  styleUrls: ['./diwali-voucher.component.css']
})
export class DiwaliVoucherComponent implements OnInit {

  readonly eventId = 'diwali';
  readonly apitoken = '609615431cd8d539b733876c7e0c511dSAGLcHZ6nxEBnE4XlJ1nmcPTZaOXOGIXW';
  readonly bannerImage = 'assets/images/breadcurmb/banner_diwali.jpeg';
  readonly giftMessage = 'Diwali Voucher 2026';

  mode: 'categories' | 'vouchers' = 'categories';
  categoryId = 0;

  userCode = '';
  memberType = '';
  bearerToken = '';

  categories: any[] = [];
  products: any[] = [];
  categoryName = '';
  remainingAmount = 0;

  loading = false;
  errorMsg = '';
  validating = false;
  placing = false;
  orderRefnos: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private cartService: DiwaliCartService,
    private authManager: AuthManagerService
  ) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);

    const stored = localStorage.getItem('profileData');
    if (!stored) {
      this.router.navigate(['/otp']);
      return;
    }

    let profile: any = null;
    try {
      profile = JSON.parse(stored);
    } catch {
      profile = null;
    }

    this.userCode = profile?.user_code || '';
    this.memberType = profile?.category || '';
    if (!this.userCode) {
      this.router.navigate(['/otp']);
      return;
    }
    this.bearerToken = localStorage.getItem('bearerToken') || '';

    this.cartService.init(this.userCode, this.eventId, this.apitoken);

    this.route.paramMap.subscribe(params => {
      const id = params.get('categoryId');
      if (id) {
        this.mode = 'vouchers';
        this.categoryId = Number(id);
        this.loadVouchers();
      } else {
        this.mode = 'categories';
        this.loadCategories();
      }
    });
  }

  get cart(): DiwaliCartLine[] {
    return this.cartService.cart;
  }

  get cartTotal(): number {
    return this.cartService.cartTotal;
  }

  get cartQty(): number {
    return this.cartService.cartQty;
  }

  get budgetPercent(): number {
    if (this.remainingAmount <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((this.cartTotal / this.remainingAmount) * 100));
  }

  loadCategories(): void {
    this.loading = true;
    this.errorMsg = '';
    this.orderRefnos = [];

    this.productService.diwaliCategories(this.userCode, this.eventId, this.apitoken).subscribe({
      next: (res: any) => {
        this.loading = false;
        const response = res?.response;
        if (response?.status === 1 && Array.isArray(response.data)) {
          this.categories = response.data;
        } else {
          this.categories = [];
          this.errorMsg = response?.msg || 'No vouchers found.';
        }
      },
      error: () => {
        this.loading = false;
        this.categories = [];
        this.errorMsg = 'Unable to load voucher categories. Please try again.';
      }
    });
  }

  loadVouchers(): void {
    this.loading = true;
    this.errorMsg = '';
    this.orderRefnos = [];
    this.products = [];
    this.categoryName = '';
    this.remainingAmount = 0;

    this.productService.fetchDiwaliProducts(this.eventId, this.categoryId, this.memberType, this.userCode, this.apitoken).subscribe({
      next: (res: any) => {
        this.loading = false;
        const response = res?.response;
        if (response?.status === 1 && Array.isArray(response.data) && response.data.length > 0) {
          this.products = response.data.map((product: any) => {
            let details: any = product.product_details;
            if (typeof details === 'string') {
              try {
                details = JSON.parse(details);
              } catch {
                details = {};
              }
            }
            return { ...product, product_details: details };
          });
          this.categoryName = this.products[0]?.voucher_category_name || '';
          this.remainingAmount = Number(this.products[0]?.remaining_voucher_amount || 0);
        } else {
          this.products = [];
          this.errorMsg = response?.msg || 'Details not found.';
        }
      },
      error: () => {
        this.loading = false;
        this.products = [];
        this.errorMsg = 'Unable to load vouchers. Please try again.';
      }
    });
  }

  openCategory(category: any): void {
    this.router.navigate(['/diwali', 'category', category.id]);
  }

  backToCategories(): void {
    this.router.navigate(['/diwali']);
  }

  addToCart(product: any, denomination: number): void {
    this.orderRefnos = [];
    if (!this.cartService.add(product, denomination, this.remainingAmount)) {
      alert('Cart total exceeds your remaining balance.');
    }
  }

  changeQty(line: DiwaliCartLine, delta: number): void {
    this.cartService.changeQty(line, delta, this.remainingAmount);
  }

  removeLine(line: DiwaliCartLine): void {
    this.cartService.remove(line);
  }

  placeOrder(): void {
    if (this.validating || this.placing) {
      return;
    }
    if (this.cart.length === 0) {
      alert('Please add at least one voucher to your cart.');
      return;
    }
    if (this.cartTotal > this.remainingAmount) {
      alert('Cart total exceeds your remaining balance.');
      return;
    }

    this.validating = true;

    const items = this.cart.map(line => ({
      voucher_product_id: line.voucher_product_id,
      denomination: line.denomination,
      qty: line.qty
    }));

    this.productService.validateDiwaliCart(this.userCode, this.eventId, items, this.apitoken).subscribe({
      next: (res: any) => {
        this.validating = false;
        const response = res?.response;
        if (response?.status === 1) {
          this.confirmAndPlace();
        } else {
          alert(response?.msg || 'Unable to validate your cart.');
        }
      },
      error: () => {
        this.validating = false;
        alert('Unable to validate your cart. Please try again.');
      }
    });
  }

  private async confirmAndPlace(): Promise<void> {
    const confirmed = confirm(`Place order for ${this.cartQty} voucher(s) worth ₹${this.cartTotal}?`);
    if (!confirmed) {
      return;
    }

    this.placing = true;
    const redemptionRef = 'DW' + Date.now();
    await this.attemptOrderPlacement([...this.cart], [], [], redemptionRef);
  }

  private async attemptOrderPlacement(
    lines: DiwaliCartLine[],
    placedLines: DiwaliCartLine[],
    refs: string[],
    redemptionRef: string,
    tokenRetried = false
  ): Promise<void> {
    let failure = '';

    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      try {
        const res: any = await firstValueFrom(
          this.productService.createOrder1(this.buildOrderForm(line, redemptionRef))
        );
        const response = res?.response ?? res;
        const { status, msg, data } = response || {};

        if (status === 0 && data?.data?.code === 401 && data.data.message === 'oauth_problem=token_rejected') {
          if (tokenRetried) {
            failure = 'Your session has expired. Please login and try again.';
            break;
          }
          await this.handleTokenRejection(lines, placedLines, refs, index, redemptionRef);
          return;
        }

        if (status === 1 || status === 'PROCESSING') {
          placedLines.push(line);
          if (response?.data?.refno) {
            refs.push(String(response.data.refno));
          }
        } else {
          failure = msg || 'Failed to place the order. Please try again.';
          break;
        }
      } catch (error) {
        if (!tokenRetried && this.getErrorMessage(error) === 'oauth_problem=token_rejected') {
          await this.handleTokenRejection(lines, placedLines, refs, index, redemptionRef);
          return;
        }
        failure = 'Failed to place the order. Please try again.';
        break;
      }
    }

    this.placing = false;
    this.cartService.removePlaced(placedLines);

    if (failure) {
      alert(failure);
      if (refs.length > 0) {
        this.orderRefnos = refs;
      }
      return;
    }

    this.orderRefnos = refs;
    alert('Your Diwali voucher order has been placed successfully! You will receive the GIFT CARD CODE through the WhatsApp BOT and registered Email ID within 2 hours.');
  }

  private async handleTokenRejection(
    lines: DiwaliCartLine[],
    placedLines: DiwaliCartLine[],
    refs: string[],
    index: number,
    redemptionRef: string
  ): Promise<void> {
    try {
      await this.authManager.updateBearerToken();
      this.bearerToken = localStorage.getItem('bearerToken') || '';
      if (!this.bearerToken) {
        throw new Error('Bearer token is empty after refresh');
      }
      await this.attemptOrderPlacement(lines.slice(index), placedLines, refs, redemptionRef, true);
    } catch (refreshError) {
      console.error('Failed to refresh token:', refreshError);
      this.placing = false;
      this.cartService.removePlaced(placedLines);
      if (refs.length > 0) {
        this.orderRefnos = refs;
      }
      alert('Your session is expired. You will be logged out now. Please login and try again.');
      this.authManager.handleTokenRejected();
    }
  }

  private getErrorMessage(error: any): string {
    if (error?.response?.data?.data?.message) {
      return error.response.data.data.message;
    }
    if (error?.error?.response?.data?.data?.message) {
      return error.error.response.data.data.message;
    }
    if (error?.response?.msg) {
      return error.response.msg;
    }
    if (error?.error?.data?.message) {
      return error.error.data.message;
    }
    return 'An unexpected error occurred. Please try again later.';
  }

  private buildOrderForm(line: DiwaliCartLine, redemptionRef: string): FormData {
    const form = new FormData();
    form.append('sku', line.sku);
    form.append('price', String(line.denomination));
    form.append('qty', String(line.qty));
    form.append('giftMessage', this.giftMessage);
    form.append('architect_code', this.userCode);
    form.append('bearerToken', this.bearerToken);
    form.append('voucher_category_id', String(line.voucher_category_id));
    form.append('member_type', this.memberType);
    form.append('sync_only', line.qty <= 4 ? 'true' : 'false');
    form.append('event_code', this.eventId);
    form.append('redemption_ref', redemptionRef);
    return form;
  }

  productImage(product: any): string {
    return product?.images || product?.product_details?.images?.base || 'assets/images/default.jpg';
  }
}
